import { useCallback, useContext, useEffect, useState } from 'react';
import styles from './quiz-lead-form.module.scss';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { validateEmail } from 'src/helpers/utils';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { trackEmailSubmitted } from 'src/analytics/Events';
import { Vertical } from 'src/constants/Verticals';
import { env } from 'src/env.mjs';

const QuizLeadForm = () => {
    const {
        quizType,
        quizName,
        quizVersion,
        currentQuestion,
        handleSubmitAnswer,
        state,
    } = useContext(QuizContext);
    const {
        questionId,
        questionText,
        questionExplanation,
        userAnswers,
        answerList,
    } = currentQuestion || {};

    const [pageTransitioning] = state.pageTransitioning;
    const [, setContinueVisible] = state.continueVisible;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const validEmail = validateEmail(email);
    // Set existing answer
    useEffect(() => {
        let newFirstName: string | null = null;
        let newLastName: string | null = null;
        let newEmail: string | null = null;
        if (userAnswers.length === 3) {
            newFirstName = userAnswers[0].answerText;
            newLastName = userAnswers[1].answerText;
            newEmail = userAnswers[2].answerText;
        }
        if (newFirstName) setFirstName(newFirstName);
        if (newLastName) setLastName(newLastName);
        if (newEmail) setEmail(newEmail);
    }, [userAnswers]);

    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (validEmail.success) {
            if (!continueDisabled && questionId) {
                handleSubmitAnswer(
                    {
                        questionId: questionId,
                        questionText: questionText,
                        answerIds: [
                            answerList[0].answerId,
                            answerList[1].answerId,
                            answerList[2].answerId,
                        ],
                        answerTexts: [firstName, lastName, email],
                    },
                    () => {
                        trackEmailSubmitted(Vertical[quizType.toUpperCase()], {
                            quiz_name: `${quizName} - ${quizVersion}`,
                            label: 'Submitted email',
                            user_created_source: `Web ${env.NEXT_PUBLIC_BRAND} ${quizVersion} ${quizName}`,
                            user_created_method: 'Email',
                        });
                    },
                );
            }
        }
    }, [
        quizType,
        quizName,
        quizVersion,
        answerList,
        firstName,
        lastName,
        email,
        continueDisabled,
        questionId,
        questionText,
        handleSubmitAnswer,
        validEmail.success,
    ]);

    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);

    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueVisible(true);
            setContinueDisabled(
                firstName === null ||
                    firstName.length === 0 ||
                    lastName === null ||
                    lastName.length === 0 ||
                    email === null ||
                    email.length === 0 ||
                    !validEmail.success ||
                    !validateEmail(email),
            );
        }
    }, [
        firstName,
        lastName,
        email,
        emailTouched,
        validEmail,
        pageTransitioning,
        setContinueDisabled,
        setContinueVisible,
    ]);

    return (
        <div className={styles['quiz-lead-form--wrapper']}>
            {/* Title */}
            <h4 className={styles['title']}>{questionText}</h4>
            <div className={styles['content--wrapper']}>
                <div className={styles['name--wrapper']}>
                    {/* First name */}
                    <div className={styles['content--column']}>
                        <label className="p2 highlight">First name</label>
                        <input
                            data-testid="first-name-input"
                            className={classNames(styles['default-field'])}
                            value={firstName || ''}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                            placeholder="First name..."
                        />
                    </div>
                    {/* Last name */}
                    <div className={styles['content--column']}>
                        <label className="p2 highlight">Last name</label>
                        <input
                            data-testid="last-name-input"
                            className={classNames(styles['default-field'])}
                            value={lastName || ''}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                            placeholder="Last name..."
                        />
                    </div>
                </div>
                <div>
                    {/* Email */}
                    <div className={styles['content--column']}>
                        <label className="p2 highlight">Email</label>
                        <input
                            data-testid="email-input"
                            className={classNames(
                                styles['default-field'],
                                !validEmail.success
                                    ? styles['error-field']
                                    : '',
                            )}
                            value={email || ''}
                            onChange={(e) => setEmail(e.target.value)}
                            onFocus={() =>
                                emailTouched && setEmailTouched(false)
                            }
                            onBlur={() =>
                                !emailTouched && setEmailTouched(true)
                            }
                            required
                            placeholder="Email..."
                        />
                        {!validEmail.success && (
                            <p
                                className={classNames(
                                    styles['text'],
                                    styles['error-message'],
                                    'p1',
                                )}
                            >
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                Please enter a valid email address
                            </p>
                        )}
                    </div>
                </div>
                {/* Already a customer
                // TODO: Re-instate when backend handles signed in users
                    <div
                        className={classNames(
                            styles['text'],
                            styles['sign-in'],
                            'p1',
                        )}
                    >
                        Already a customer?{' '}
                        <a href={PortalRoutes.USER_SIGN_IN}>Click here to sign in</a>
                    </div>
                */}
                <hr />
                {/* Explanation */}
                <div
                    className={classNames(
                        styles['text'],
                        styles['explanation'],
                        'p1',
                    )}
                >
                    {questionExplanation}
                </div>
            </div>
        </div>
    );
};

export default QuizLeadForm;
